import React, { useState, useEffect, useRef } from 'react';
import { X, Check, Copy, Sparkles, ShieldCheck, Zap, Tag } from 'lucide-react';
import { useApp } from '../context/AppContext';

interface PracticeTestAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartPractice?: () => void;
  onApplyCoupon?: (code: string) => void;
  targetClass?: number;
  targetSubject?: string;
}

export const PracticeTestAccessModal: React.FC<PracticeTestAccessModalProps> = ({
  isOpen,
  onClose,
  onStartPractice,
  onApplyCoupon,
  targetClass = 6,
  targetSubject = 'Science',
}) => {
  const { user, promotions } = useApp();
  const [copied, setCopied] = useState(false);
  const [, setDiscountApplied] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Body scroll lock & Escape key handling
  useEffect(() => {
    if (!isOpen) return;

    // Prevent background scrolling while modal is open
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    document.body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }

    // Keyboard accessibility: Escape to close
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Auto-focus the close button or modal when opened
    setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 50);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Determine active class: prioritize targetClass explicitly passed from clicked card or configurator
  const activeClass = targetClass || (user?.currentClass ? (Number(user.currentClass.replace(/\D/g, '')) || 6) : 6);

  // Resolve Subject Key and Course ID
  const subNameLower = (targetSubject || 'Science').toLowerCase();
  let subKey = 'sci';
  let cleanSubjectName = 'Science';
  if (subNameLower.includes('math')) {
    subKey = 'math';
    cleanSubjectName = 'Mathematics';
  } else if (subNameLower.includes('social') || subNameLower.includes('soc') || subNameLower.includes('history') || subNameLower.includes('geo')) {
    subKey = 'social';
    cleanSubjectName = 'Social Science';
  }
  const courseId = `${activeClass}-${subKey}`;

  // Default plans pricing fallback
  const defaultPrices: { [key: string]: { sellingPrice: number; originalPrice: number; title: string } } = {
    '6-sci': { sellingPrice: 149, originalPrice: 499, title: 'NCERT Class 6 Science' },
    '6-math': { sellingPrice: 149, originalPrice: 499, title: 'NCERT Class 6 Mathematics' },
    '6-social': { sellingPrice: 149, originalPrice: 499, title: 'NCERT Class 6 Social Science' },
    '7-sci': { sellingPrice: 149, originalPrice: 499, title: 'NCERT Class 7 Science' },
    '7-math': { sellingPrice: 149, originalPrice: 499, title: 'NCERT Class 7 Mathematics' },
    '7-social': { sellingPrice: 149, originalPrice: 499, title: 'NCERT Class 7 Social Science' },
    '8-sci': { sellingPrice: 149, originalPrice: 499, title: 'NCERT Class 8 Science' },
    '8-math': { sellingPrice: 149, originalPrice: 499, title: 'NCERT Class 8 Mathematics' },
    '8-social': { sellingPrice: 149, originalPrice: 499, title: 'NCERT Class 8 Social Science' },
  };

  // Get dynamic pricing from Admin promotions settings
  let sellingPrice = 149;
  let originalPrice = 499;

  const isFreeLaunch = promotions?.free_premium_active === 'true';

  if (isFreeLaunch) {
    sellingPrice = 0;
  } else if (promotions?.membership_plans && promotions.membership_plans.length > 0) {
    const matchedPlan = promotions.membership_plans.find((p: any) => p.id === courseId);
    if (matchedPlan) {
      sellingPrice = Number(matchedPlan.sellingPrice ?? 149);
      originalPrice = Number(matchedPlan.originalPrice ?? 499);
    } else if (defaultPrices[courseId]) {
      sellingPrice = defaultPrices[courseId].sellingPrice;
      originalPrice = defaultPrices[courseId].originalPrice;
    }
  } else if (defaultPrices[courseId]) {
    sellingPrice = defaultPrices[courseId].sellingPrice;
    originalPrice = defaultPrices[courseId].originalPrice;
  }

  const isPromoActive = promotions?.promo_banner_active === 'true' || promotions?.promo_banner_active === '1';
  const couponCode = (isPromoActive && promotions?.promo_banner_code) ? promotions.promo_banner_code.trim() : '';
  const discountText = (isPromoActive && promotions?.promo_banner_slogan) ? promotions.promo_banner_slogan.trim() : '';
  const badgeText = (isPromoActive && promotions?.promo_banner_badge) ? promotions.promo_banner_badge.trim() : '';

  const handleCopyCode = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(couponCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePrimaryClick = () => {
    if (onApplyCoupon) {
      onApplyCoupon(couponCode);
    }
    setDiscountApplied(true);
    if (onStartPractice) {
      onStartPractice();
    } else {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="premium-access-modal-title"
    >
      {/* Click outside backdrop to close */}
      <div 
        className="absolute inset-0 cursor-pointer" 
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Main Modal Container: Centered Rounded Card Overlay on Mobile & Desktop */}
      <div 
        ref={modalRef}
        className="relative w-[95%] sm:w-full max-w-[480px] max-h-[88vh] sm:max-h-[90vh] bg-[#FEFCE8] text-slate-900 rounded-2xl sm:rounded-[28px] shadow-2xl shadow-amber-950/50 border-2 border-amber-200/90 flex flex-col justify-between overflow-hidden z-10 transition-all my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* STICKY HEADER: Fixed on both Mobile and Desktop */}
        <header className="sticky top-0 z-30 bg-[#FEFCE8]/95 backdrop-blur-md border-b border-amber-200/70 px-3.5 py-2.5 sm:px-6 sm:py-3.5 flex items-center justify-between shrink-0 select-none">
          {/* Close Button with Touch Target */}
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="p-2 sm:p-2.5 text-slate-700 hover:text-slate-900 bg-amber-100/90 hover:bg-amber-200 active:bg-amber-300 rounded-full transition-colors cursor-pointer flex items-center justify-center shadow-xs focus:outline-none focus:ring-2 focus:ring-amber-500"
            aria-label="Close modal"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          {/* Premium Practice Access Badge / Ribbon */}
          <div className="bg-gradient-to-r from-[#F59E0B] via-amber-400 to-[#EA580C] text-slate-950 font-black text-[9px] xs:text-[10px] sm:text-[11px] tracking-wider uppercase px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-full shadow-xs border border-amber-300/80 flex items-center gap-1 sm:gap-1.5 whitespace-nowrap shrink-0">
            <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-950 fill-slate-950" />
            <span>PREMIUM PRACTICE ACCESS</span>
          </div>
        </header>

        {/* SCROLLABLE CONTENT BODY */}
        <main className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-7 py-3.5 sm:py-5 space-y-3.5 sm:space-y-4 text-left custom-scrollbar">
          {/* Platform Branding Badge & Dynamic Class / Subject Tag */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#0B1B3D] text-white text-[10px] font-extrabold uppercase tracking-widest rounded-full shadow-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse"></span>
              MCQ PULSE™
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-200/70 border border-amber-300 text-amber-950 text-[10px] font-extrabold uppercase tracking-wide rounded-full">
              Class {activeClass} • {cleanSubjectName}
            </span>
          </div>

          {/* Heading */}
          <h2 
            id="premium-access-modal-title" 
            className="font-display font-black text-xl sm:text-[26px] text-[#0B1B3D] leading-tight tracking-tight"
          >
            Ready to Test Your Knowledge?
          </h2>

          {/* Supporting Message */}
          <p className="text-slate-700 text-xs sm:text-sm leading-relaxed font-medium">
            Start today’s <span className="font-bold text-[#0B1B3D]">Class {activeClass} {cleanSubjectName}</span> practice test and strengthen your concepts with instant performance feedback, detailed explanations, and smart progress tracking.
          </p>

          {/* Course Subject & Price Tag Bar */}
          <div className="flex items-center justify-between bg-amber-100/90 border border-amber-300/90 rounded-xl px-3.5 sm:px-4 py-2 sm:py-2.5 shadow-xs gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Tag className="w-4 h-4 text-[#D97706] shrink-0" />
              <span className="text-xs sm:text-sm font-bold text-amber-950 truncate">
                Class {activeClass} {cleanSubjectName}
              </span>
            </div>
            <div className="flex items-baseline gap-1.5 sm:gap-2 shrink-0">
              {originalPrice > sellingPrice && (
                <span className="text-xs text-slate-500 line-through font-semibold">
                  ₹{originalPrice}
                </span>
              )}
              <span className="text-sm sm:text-base font-black text-[#EA580C]">
                {sellingPrice === 0 ? 'FREE' : `₹${sellingPrice}`}
              </span>
            </div>
          </div>

          {/* HIGHLIGHTED PROMOTIONAL SECTION / OFFER CARD */}
          {isPromoActive && couponCode && (
            <div className="p-3.5 sm:p-5 bg-white/95 border-2 border-amber-200/90 rounded-2xl text-center space-y-2.5 sm:space-y-3 shadow-xs">
              {/* Launch Offer Title */}
              {badgeText && (
                <div className="text-[11px] sm:text-xs font-black uppercase tracking-widest text-[#D97706] flex items-center justify-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-[#D97706]" />
                  <span>{badgeText}</span>
                </div>
              )}

              {/* Coupon Code Box */}
              <div className="relative flex items-center justify-between gap-2 bg-amber-50/80 border-2 border-dashed border-[#FDBA74] rounded-xl px-3 sm:px-4 py-2 shadow-xs max-w-[290px] mx-auto w-full group">
                <div className="text-left">
                  <span className="block text-[8px] sm:text-[9px] font-bold text-slate-500 uppercase tracking-wider">Coupon Code</span>
                  <span className="font-mono font-black text-xl sm:text-3xl text-[#EA580C] tracking-widest leading-none">
                    {couponCode}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleCopyCode}
                  className="min-h-[36px] sm:min-h-[40px] px-2.5 sm:px-3 py-1.5 bg-white hover:bg-amber-100 active:bg-amber-200 text-[#EA580C] rounded-lg text-xs font-black transition-all flex items-center gap-1 border border-amber-300/80 cursor-pointer shadow-xs shrink-0 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  title="Copy Coupon Code"
                  aria-label={`Copy coupon code ${couponCode}`}
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-600 stroke-[3]" />
                      <span className="text-emerald-700 font-black">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5 stroke-[2.5]" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>

              {/* Discount Headline */}
              {discountText && (
                <div className="text-xs sm:text-base font-black text-[#C2410C] tracking-wide uppercase">
                  {discountText}
                </div>
              )}

              {/* Reassuring Line */}
              <p className="text-[10px] sm:text-xs text-slate-600 font-medium">
                Unlock your premium practice experience today.
              </p>
            </div>
          )}
        </main>

        {/* STICKY FOOTER: Fixed on both Mobile and Desktop */}
        <footer className="sticky bottom-0 z-30 bg-[#FEFCE8]/95 backdrop-blur-md border-t border-amber-200/70 px-3.5 py-2.5 sm:px-7 sm:py-4 space-y-2 shrink-0 select-none">
          {/* Action Buttons */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Secondary Button: MAYBE LATER */}
            <button
              type="button"
              onClick={onClose}
              className="flex-1 min-h-[44px] sm:min-h-[46px] py-2.5 sm:py-3 px-2 sm:px-3 border-2 border-amber-300 hover:border-amber-400 active:border-amber-500 bg-amber-100/70 hover:bg-amber-100 active:bg-amber-200 text-amber-950 font-black text-[11px] sm:text-xs tracking-wider uppercase rounded-xl transition-all duration-150 cursor-pointer flex items-center justify-center text-center focus:outline-none focus:ring-2 focus:ring-amber-400"
            >
              MAYBE LATER
            </button>

            {/* Primary Button: UNLOCK NOW */}
            <button
              type="button"
              onClick={handlePrimaryClick}
              className="flex-1 min-h-[44px] sm:min-h-[46px] py-2.5 sm:py-3 px-2 sm:px-3 bg-gradient-to-r from-[#F59E0B] via-amber-500 to-[#EA580C] hover:from-amber-400 hover:to-orange-600 text-slate-950 font-black text-[11px] sm:text-xs tracking-wider uppercase rounded-xl shadow-md shadow-amber-500/30 hover:shadow-amber-500/50 transition-all duration-150 cursor-pointer active:scale-[0.98] flex items-center justify-center gap-1 sm:gap-1.5 text-center focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <span className="whitespace-nowrap">UNLOCK NOW</span>
              <Zap className="w-3.5 h-3.5 fill-slate-950 shrink-0" />
            </button>
          </div>

          {/* Security / Trust Footer Line */}
          <div className="text-center flex items-center justify-center gap-1 text-[9px] sm:text-[10px] text-amber-900/70 font-bold uppercase tracking-wider">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
            <span>MCQ PULSE LEARNING SOLUTIONS</span>
          </div>
        </footer>
      </div>
    </div>
  );
};

